import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';

import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '../components/ui';
import { getToolGroups, openTool } from '../layout/navigation';

interface HomePageProps {
  /** Admins also see the admin tools, as in the sidebar */
  isAdmin?: boolean;
}

export const HomePage: React.FC<HomePageProps> = ({ isAdmin = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toolGroups = getToolGroups(t, isAdmin);

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full flex flex-col items-center justify-center min-h-[calc(100vh-80px)]">
      <div className="w-full max-w-4xl">
        <div className="mb-8 lg:mb-12 text-center">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-3">
            {t('dashboard.welcome')}
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-muted-foreground">
            {t('dashboard.title')}
          </p>
        </div>

        <div className="space-y-8 lg:space-y-10">
          {toolGroups.map((group) => (
            <section key={group.id}>
              <h2 className="mb-3 sm:mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {group.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {group.tools.map((tool) => {
                  const IconComponent = tool.icon;
                  return (
                    <Card
                      key={tool.id}
                      className="hover:shadow-lg transition-all duration-200 cursor-pointer flex flex-col"
                    >
                      <CardHeader>
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-lg sm:text-xl">
                              {tool.title}
                            </CardTitle>
                            <CardDescription className="text-sm">
                              {tool.description}
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="mt-auto">
                        <Button
                          onClick={() => openTool(tool, navigate)}
                          className="w-full gap-2 text-sm sm:text-base"
                        >
                          <IconComponent className="w-4 h-4" />
                          {tool.title}
                          {tool.external && (
                            <ExternalLink className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};
